import { poissonLowerCount } from "./discoveryPatternScore.js";
import { runDiscoveryRead } from "./discoveryReadQueue.js";
import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { findingValueLabel, discoveryRelationshipGroupsSpec, discoveryOverlapMatrixSpec, discoveryTotalsSpec, discoveryPatternTotalsSpec, discoveryPreviousRange, discoveryComparisonSupported, DISCOVERY_SEVERITIES } from "./discoveryQueries.js";

// Behaviour severity is deliberately absent: it rates a finding rather than
// naming a kind of activity, so "X alongside medium severity" says nothing an
// analyst can act on, and the card already shows the overlap's severity mix.
// Choosing two fields by hand can still compare it.
export const PATTERN_AXES = ["reason", "sensitivity", "policy", "norms", "intent", "behaviour", "content"];
export function patternAxes(available) {
    const ids = new Set(available.map((axis) => axis.id));
    return PATTERN_AXES.filter((id) => ids.has(id));
}
export const PATTERN_CANDIDATES = 12;
const patternError = (message) => Object.assign(new Error(message), { name: "DiscoveryPatternError" });
const ignored = new Set(["", "none", "no_view", "unspecified", "consistent", "other"]);
const coupled = new Set(["reason:sensitivity", "policy:reason", "intent:reason", "intent:norms", "content:sensitivity", "behaviour:behaviour", "content:content"]);
export const termKey = (term) => JSON.stringify([term.axis, term.value]);
const pairKey = (a, b) => [termKey(a), termKey(b)].sort().join("|");
const byPrevalence = (a, b) => b.sessions - a.sessions || termKey(a).localeCompare(termKey(b));

// Whether a pair has happened here before, and which way it is moving. A move
// must clear both an absolute and a proportional change, so one session
// becoming three is not reported as a rise. Null means the previous period
// could not be lined up and the caller should say so rather than guess.
const PRECEDENT_MOVE = 3, PRECEDENT_RISE = 1.5, PRECEDENT_FALL = 0.67;
export function patternPrecedence(pattern) {
    const previous = Number(pattern?.previousSessions), now = Number(pattern?.sessions);
    if (!Number.isFinite(previous) || !Number.isFinite(now)) return null;
    if (previous === 0) return "new";
    if (now - previous >= PRECEDENT_MOVE && now >= PRECEDENT_RISE * previous) return "rising";
    if (previous - now >= PRECEDENT_MOVE && now <= PRECEDENT_FALL * previous) return "falling";
    return "steady";
}

export function patternExampleSessions(rows) {
    const sessions = [...new Map(rows.filter((r) => r.trace_id).map((r) => [r.trace_id, r])).values()];
    const endpoints = new Set(), chosen = [];
    for (const row of sessions) {
        if (endpoints.has(row.endpoint_id)) continue;
        endpoints.add(row.endpoint_id); chosen.push(row);
        if (chosen.length === 3) return chosen;
    }
    return [...chosen, ...sessions.filter((row) => !chosen.includes(row))].slice(0, 3);
}

// These phrases name a kind of activity, not a verified action.
const phrases = {
    reason: { risk: "risk findings", sensitivity: "sensitive content", policy: "policy findings", off_mission: "off-mission activity" },
    policy: { violation: "policy violations", consistent: "activity consistent with policy" },
    norms: { deviation: "departures from expected behaviour", consistent: "activity consistent with expected behaviour" },
    intent: { aligned: "activity aligned with user intent", counter: "activity counter to user intent", tangential: "activity tangential to user intent", advances: "activity aligned with user intent" },
};
function patternPhrase(term) {
    if (term.axis === "severity") return `${findingValueLabel(term.value).toLowerCase()} severity`;
    return phrases[term.axis]?.[term.value] ?? findingValueLabel(term.value);
}
export function patternLabel(term) {
    const phrase = patternPhrase(term);
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}
export function patternTitle(pattern) {
    return `${patternLabel(pattern.a)} alongside ${patternPhrase(pattern.b)}`;
}

// Absent, unassessed and ubiquitous labels cannot describe an overlap: a term
// carried by every Prompt Session in scope pairs with everything by
// construction. Both modes drop them, so the manual comparison cannot open on a
// pair that is really one term's own population.
export function eligiblePatternTerms(facets, sessionCount) {
    return facets.map(({ axis, rows }) => rows.filter((r) => typeof r.value === "string" && !ignored.has(r.value)
        && Number(r.sessions) > 0 && Number(r.sessions) < sessionCount)
        .map((r) => ({ axis, value: r.value, sessions: Number(r.sessions) })).sort(byPrevalence));
}

export function choosePatternCandidates(facets, sessionCount) {
    const eligible = eligiblePatternTerms(facets, sessionCount);
    const first = eligible.flatMap((terms) => terms.slice(0, 1));
    return [...first, ...eligible.flatMap((terms) => terms.slice(1)).sort(byPrevalence).slice(0, PATTERN_CANDIDATES - first.length)].sort((a, b) => termKey(a).localeCompare(termKey(b)));
}

// A pair that contains every session of one term is exactly that term's
// population. Equal aggregate counts without this containment prove nothing.
export function consolidatePatternPopulations(patterns, observations) {
    const parent = new Map();
    function root(key) { if (!parent.has(key)) parent.set(key, key); const next = parent.get(key); if (next !== key) parent.set(key, root(next)); return parent.get(key); }
    for (const { source, target, sessions } of observations) {
        if (sessions > 0 && sessions === source.sessions && sessions === target.sessions) parent.set(root(termKey(source)), root(termKey(target)));
    }
    const populations = new Map();
    for (const pattern of patterns) {
        const anchor = [pattern.a, pattern.b].find((term) => term.sessions === pattern.sessions);
        const key = anchor ? root(termKey(anchor)) : pattern.key;
        if (populations.has(key)) populations.get(key).relatedPatterns.push(pattern);
        else populations.set(key, { ...pattern, relatedPatterns: [] });
    }
    return [...populations.values()];
}

export function rankPatterns(candidates, observations, sessionCount, { manual = false, order = "largest" } = {}) {
    const pairs = new Map(), lowerCounts = new Map();
    for (const { source, target, sessions } of observations) {
        if (termKey(source) === termKey(target) || !Number.isFinite(sessions) || sessions <= 0) continue;
        if (sessions > Math.min(source.sessions, target.sessions) || Math.max(source.sessions, target.sessions) > sessionCount) throw patternError("Finding counts changed. Refresh to compare this scope again.");
        if (!manual && coupled.has([source.axis, target.axis].sort().join(":"))) continue;
        const key = pairKey(source, target);
        if (pairs.has(key)) {
            if (pairs.get(key).sessions !== sessions) throw patternError("Finding counts changed. Refresh to compare this scope again.");
            continue;
        }
        const [a, b] = [source, target].sort((x, y) => termKey(x).localeCompare(termKey(y)));
        const expected = a.sessions * b.sessions / sessionCount;
        if (!lowerCounts.has(sessions)) lowerCounts.set(sessions, poissonLowerCount(sessions));
        pairs.set(key, { key, a, b, sessions, expected, score: lowerCounts.get(sessions) / expected, lift: sessions / expected, shareA: sessions / a.sessions, shareB: sessions / b.sessions });
    }
    const largest = [...pairs.values()].sort((a, b) => b.sessions - a.sessions || b.lift - a.lift || a.key.localeCompare(b.key));
    const distinctiveOrder = (a, b) => b.score - a.score || b.sessions - a.sessions || a.key.localeCompare(b.key);
    const qualifies = (p) => p.sessions >= 5 && p.score >= 1.5;
    const distinctive = largest.filter(qualifies).sort(distinctiveOrder);
    if (manual) return { patterns: (order === "distinctive" ? distinctive : largest).slice(0, 6), candidateCount: candidates.length };
    const populations = consolidatePatternPopulations([...distinctive, ...largest.filter((p) => !qualifies(p))], observations);
    const featured = populations.filter(qualifies).slice(0, 3).map((p) => ({ ...p, kind: "Distinctive overlap" }));
    const used = new Set(featured.map((p) => p.key));
    featured.push(...populations.filter((p) => !used.has(p.key)).sort((a, b) => b.sessions - a.sessions || a.key.localeCompare(b.key)).slice(0, 3).map((p) => ({ ...p, kind: "Common overlap" })));
    return { patterns: featured, candidateCount: candidates.length };
}

async function boundedMap(items, work, signal) {
    const results = [];
    for (let i = 0; i < items.length; i += 2) {
        signal?.throwIfAborted();
        results.push(...await Promise.all(items.slice(i, i + 2).map(work)));
    }
    signal?.throwIfAborted();
    return results;
}

export async function loadDiscoveryPatterns({ timeRange, filters, axes, sessionCount, manual = false, order, signal, contextKey, query = runQuery }) {
    if (axes.length > PATTERN_AXES.length || new Set(axes).size !== axes.length) throw patternError("Too many comparison fields.");
    const read = async (spec) => {
        signal?.throwIfAborted();
        const rows = rowsToObjects(await runDiscoveryRead(() => query(spec, { signal, requestContext: contextKey }), { signal }));
        signal?.throwIfAborted();
        return rows;
    };
    const facets = await boundedMap(axes, async (axis) => ({ axis, rows: await read(discoveryRelationshipGroupsSpec({ timeRange, filters, axis })) }), signal);
    if (facets.some(({ rows }) => rows.some((r) => !Number.isSafeInteger(Number(r.sessions)) || Number(r.sessions) < 0 || Number(r.sessions) > sessionCount))) throw patternError("Incomplete finding counts. Refresh to try again.");
    const candidates = manual ? eligiblePatternTerms(facets, sessionCount).flat() : choosePatternCandidates(facets, sessionCount);
    if (candidates.length < 2) return { patterns: [], candidateCount: candidates.length };
    const sourceAxes = [...new Set(candidates.map((t) => t.axis))];
    const observations = (await boundedMap(manual ? axes.slice(0, 1) : sourceAxes, async (axis) => {
        const sources = candidates.filter((t) => t.axis === axis);
        const targets = manual && axes.length === 2 ? candidates.filter((t) => t.axis === axes[1]) : candidates;
        if (!sources.length || targets.length < (axes.length === 1 ? 2 : 1)) return [];
        const rows = await read(discoveryOverlapMatrixSpec({ timeRange, filters, axis, sources: sources.map((t) => t.value), targets }));
        return rows.flatMap((r) => {
            const source = sources.find((t) => t.value === r.value);
            if (!source) throw patternError("Unexpected overlap group. Refresh to try again.");
            return targets.map((target, i) => {
                const sessions = Number(r[`overlap_${i}`]);
                if (!Number.isSafeInteger(sessions) || sessions < 0) throw patternError("Incomplete overlap counts. Refresh to try again.");
                return { source, target, sessions };
            });
        });
    }, signal)).flat();
    const result = rankPatterns(candidates, observations, sessionCount, { manual, order });
    // A topic constraint cannot be lined up against an earlier window, so the
    // previous period is read only when the scope supports the comparison.
    let previousRange = null;
    try { if (discoveryComparisonSupported(axes[0] ?? "reason", filters)) previousRange = discoveryPreviousRange(timeRange); }
    catch { previousRange = null; }
    result.patterns = await boundedMap(result.patterns, async (pattern) => {
        const relationship = { terms: [pattern.a, pattern.b], filters, expanded: false };
        const [totals] = await read(discoveryPatternTotalsSpec(timeRange, relationship));
        if (!totals || Number(totals.sessions) !== pattern.sessions) throw patternError("Finding counts changed. Refresh to compare this scope again.");
        const severityCounts = DISCOVERY_SEVERITIES.map((value) => Number(totals[`severity_${value}`]));
        if (![totals.endpoints, totals.findings, ...severityCounts].every((n) => Number.isSafeInteger(Number(n)) && Number(n) >= 0)) throw patternError("Incomplete finding counts. Refresh to try again.");
        const severity = Object.fromEntries(DISCOVERY_SEVERITIES.map((value, i) => [value, severityCounts[i]]));
        const measured = { ...pattern, endpoints: Number(totals.endpoints), findings: Number(totals.findings), severity, high: severity.high + severity.critical, relationship };
        if (!previousRange) return measured;
        const [before] = await read(discoveryTotalsSpec(previousRange, [], relationship));
        const previousSessions = Number(before?.sessions ?? 0);
        return Number.isSafeInteger(previousSessions) && previousSessions >= 0 ? { ...measured, previousSessions } : measured;
    }, signal);
    result.patterns.sort((a, b) => {
        if (!manual && a.kind !== b.kind) return a.kind === "Distinctive overlap" ? -1 : 1;
        const distinctive = manual ? order === "distinctive" : a.kind === "Distinctive overlap";
        return (distinctive ? b.score - a.score : b.sessions - a.sessions) || b.endpoints - a.endpoints || b.sessions - a.sessions || a.key.localeCompare(b.key);
    });
    return result;
}
